import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

/* Receives a date and returns a human readable date string
 * following the rules below:
 * - Just now (less than 1 min ago)
 * - "_min ago" or "_ minutes ago" (fewer than 60 mins ago)
 * - "_h ago" or "_ hours ago" (fewer than 24 hours ago)
 * - "_d ago" or "_ days ago" (fewer than 7 days ago)
 * - "_w ago" or "_ weeks ago" (fewer than 1 month, 30 days, ago)
 * - "_m ago" or "_ months ago" (fewer than 1 year, 365 days, ago)
 * - "_y ago" or "_ years ago" (more than 1 year ago)
 */

const ONE_MINUTE_MS = 1000 * 60;
const ONE_HOUR_MS = ONE_MINUTE_MS * 60;
const ONE_DAY_MS = ONE_HOUR_MS * 24;
const ONE_WEEK_MS = ONE_DAY_MS * 7;
const ONE_MONTH_MS = ONE_DAY_MS * 30;
const ONE_YEAR_MS = ONE_DAY_MS * 365;

const getTense = (n: number, abb: boolean) => {
  if (abb) {
    return 'abbreviated';
  }
  return n > 1 ? 'other' : 'one';
};

const useFormattedTimeAgo = (
  date?: Date | string | null,
  abbreviate: boolean = true,
) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);

  if (!date) return '';

  const dateCreated = new Date(date);
  const now = new Date();
  const diff = now.getTime() - dateCreated.getTime();

  let key:
    | 'justNow'
    | 'minutes'
    | 'hours'
    | 'days'
    | 'weeks'
    | 'months'
    | 'years';
  let units: number = 0;

  if (diff < ONE_MINUTE_MS) {
    return t('uploadedImage.timeAgo.justNow');
  }
  if (diff < ONE_HOUR_MS) {
    key = 'minutes';
    units = Math.floor(diff / ONE_MINUTE_MS);
  } else if (diff < ONE_DAY_MS) {
    key = 'hours';
    units = Math.floor(diff / ONE_HOUR_MS);
  } else if (diff < ONE_WEEK_MS) {
    key = 'days';
    units = Math.floor(diff / ONE_DAY_MS);
  } else if (diff < ONE_MONTH_MS) {
    key = 'weeks';
    units = Math.floor(diff / ONE_WEEK_MS);
  } else if (diff < ONE_YEAR_MS) {
    key = 'months';
    units = Math.floor(diff / ONE_MONTH_MS);
  } else {
    key = 'years';
    units = Math.floor(diff / ONE_YEAR_MS);
  }
  return t(`uploadedImage.timeAgo.${key}.${getTense(units, abbreviate)}`, {
    count: units,
  });
};

export { useFormattedTimeAgo };
